// Renderização de mensagens — módulo único usado no front (prévia) e no back (fonte da verdade). RN-TPL-03

export const VARIAVEIS = [
  'nome',
  'primeiro_nome',
  'empresa',
  'cidade',
  'vendedor',
  'dias_sem_compra',
  'valor_ultima_compra',
  'empresa_vendedora',
] as const;

export type Variavel = (typeof VARIAVEIS)[number];
export type ContextoRender = Partial<Record<Variavel, string | null>>;

const PADRAO = /\{\{\s*([a-z_]+)\s*(?:\|([^}]*))?\}\}/g;

export function extrairVariaveis(corpo: string): string[] {
  const nomes = new Set<string>();
  for (const m of corpo.matchAll(PADRAO)) nomes.add(m[1]!);
  return [...nomes];
}

export function variaveisDesconhecidas(corpo: string): string[] {
  return extrairVariaveis(corpo).filter((v) => !(VARIAVEIS as readonly string[]).includes(v));
}

export interface ResultadoRender {
  texto: string;
  /** Variáveis sem valor e sem fallback (RN-TPL-02) */
  faltando: string[];
}

export function renderizar(corpo: string, ctx: ContextoRender): ResultadoRender {
  const faltando = new Set<string>();
  const texto = corpo.replace(PADRAO, (_m, nome: string, fallback?: string) => {
    const valor = ctx[nome as Variavel];
    if (valor !== undefined && valor !== null && String(valor).trim() !== '') return String(valor);
    if (fallback !== undefined) return fallback.trim();
    faltando.add(nome);
    return '';
  });
  return { texto: texto.replace(/[ \t]{2,}/g, ' ').trim(), faltando: [...faltando] };
}

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function capitalizar(p: string): string {
  return p ? p.charAt(0).toLocaleUpperCase('pt-BR') + p.slice(1).toLocaleLowerCase('pt-BR') : p;
}

/** Diferença em dias entre duas datas ISO (YYYY-MM-DD), sem fuso. */
export function diasEntre(deISO: string, ateISO: string): number {
  const [a1, m1, d1] = deISO.split('-').map(Number);
  const [a2, m2, d2] = ateISO.split('-').map(Number);
  return Math.round((Date.UTC(a2!, m2! - 1, d2!) - Date.UTC(a1!, m1! - 1, d1!)) / 86_400_000);
}

export interface DadosContexto {
  cliente: {
    nome: string;
    empresa?: string | null;
    cidade?: string | null;
    ultimaCompra?: string | null; // YYYY-MM-DD
    valorUltimaCompra?: string | number | null;
  };
  vendedorNome?: string | null;
  empresaVendedora?: string | null;
  /** Hoje no fuso do tenant (YYYY-MM-DD) */
  hoje: string;
}

export function montarContexto(d: DadosContexto): ContextoRender {
  const primeiro = d.cliente.nome.trim().split(/\s+/)[0] ?? '';
  const valor = d.cliente.valorUltimaCompra;
  return {
    nome: d.cliente.nome.trim(),
    primeiro_nome: capitalizar(primeiro),
    empresa: d.cliente.empresa ?? null,
    cidade: d.cliente.cidade ?? null,
    vendedor: d.vendedorNome ? d.vendedorNome.trim().split(/\s+/)[0]! : null,
    dias_sem_compra: d.cliente.ultimaCompra ? String(diasEntre(d.cliente.ultimaCompra, d.hoje)) : null,
    valor_ultima_compra: valor !== null && valor !== undefined && valor !== '' ? moeda.format(Number(valor)) : null,
    empresa_vendedora: d.empresaVendedora ?? null,
  };
}
